// Pure state and validation for the scorecard fill form, shared between the
// score modal and the standalone evaluator page.

export type ScorecardQuestionType =
  | 'rating'
  | 'yes_no'
  | 'dropdown'
  | 'free_text'
  | 'recommendation';

export interface ScorecardQuestion {
  id: number;
  text: string;
  description?: string | null;
  question_type: ScorecardQuestionType; // eslint-disable-line camelcase
  required: boolean;
  allow_notes: boolean; // eslint-disable-line camelcase
  rating_max: number; // eslint-disable-line camelcase
  options: string[];
  hint_text?: string | null; // eslint-disable-line camelcase
  // Percent of the template total; 0 unless the scorecard is weighted.
  weight?: number;
}

export interface ScorecardFillContext {
  template: {
    id: number;
    name: string;
    template_type: 'unweighted' | 'weighted' | 'text'; // eslint-disable-line camelcase
    questions: ScorecardQuestion[];
  };
  // Contact fields and the resume arrive only for a user with real applicant
  // access; an invited evaluator gets id and name alone.
  applicant: {
    id: string;
    name: string;
    email?: string | null;
    phone_number?: string | null; // eslint-disable-line camelcase
    resume?: { title: string; url: string } | null;
  };
  opening_title: string | null; // eslint-disable-line camelcase
  // The stage this scorecard instance sits on; type and short_type drive the
  // stage icon in the modal's applicant panel.
  stage: {
    title: string | null;
    type: string;
    short_type?: string | null; // eslint-disable-line camelcase
  } | null;
  result: ScorecardResult | null;
}

// The question as the evaluator answered it — the submission snapshot, or
// the live question for answers from before snapshotting. Null only when a
// pre-snapshot answer's question was deleted.
export interface AnsweredQuestion {
  // The question id, stable across edits and deletion; null only for a
  // pre-id snapshot of a since-deleted question.
  id?: number | null;
  text: string | null;
  question_type: ScorecardQuestionType | null; // eslint-disable-line camelcase
  // Zero-based display order at submission time.
  position: number | null;
}

export interface ScorecardAnswer {
  // Null once the question is deleted; the `question` snapshot is all that
  // still describes it.
  question_id: number | null; // eslint-disable-line camelcase
  score?: number | null;
  value?: string | null;
  note?: string | null;
  question?: AnsweredQuestion | null;
}

export interface ScorecardResult {
  id: number;
  status: 'draft' | 'submitted';
  score: number;
  submitted_at: string | null; // eslint-disable-line camelcase
  answers: ScorecardAnswer[];
}

export interface AnswerDraft {
  score?: number | null;
  value?: string | null;
  note?: string | null;
}

export type DraftMap = Record<number, AnswerDraft>;

export const buildDraft = (answers: ScorecardAnswer[] = []): DraftMap =>
  answers.reduce<DraftMap>(
    (draft, answer) =>
      answer.question_id === null || answer.question_id === undefined
        ? draft
        : {
            ...draft,
            [answer.question_id]: {
              score: answer.score ?? null,
              value: answer.value ?? null,
              note: answer.note ?? null,
            },
          },
    {},
  );

// The write payload: a draft always keys by a live question id.
export const toAnswersPayload = (
  draft: DraftMap,
): (ScorecardAnswer & { question_id: number })[] => // eslint-disable-line camelcase
  Object.entries(draft).map(([questionId, answer]) => ({
    question_id: Number(questionId),
    score: answer.score ?? null,
    value: answer.value ?? null,
    note: answer.note ?? null,
  }));

export const isAnswered = (
  question: ScorecardQuestion,
  answer?: AnswerDraft,
): boolean => {
  if (!answer) return false;
  if (question.question_type === 'rating')
    return answer.score !== null && answer.score !== undefined;

  return Boolean(answer.value && answer.value.trim());
};

export const missingRequiredIds = (
  questions: ScorecardQuestion[],
  draft: DraftMap,
): number[] =>
  questions
    .filter(
      question =>
        question.required && !isAnswered(question, draft[question.id]),
    )
    .map(question => question.id);

export const draftHasContent = (draft: DraftMap): boolean =>
  Object.values(draft).some(answer =>
    answer.score !== null && answer.score !== undefined
      ? true
      : Boolean(answer.value?.trim()) || Boolean(answer.note?.trim()),
  );

// The recommendation renders as its own block after the numbered questions.
export const splitQuestions = (questions: ScorecardQuestion[]) => ({
  regular: questions.filter(
    question => question.question_type !== 'recommendation',
  ),
  recommendation: questions.find(
    question => question.question_type === 'recommendation',
  ),
});

export const ratingOptions = (question: ScorecardQuestion): number[] =>
  Array.from({ length: question.rating_max }, (_, index) => index + 1);

// The running total shown while the evaluator fills in, recalculated from
// the draft with the same rating-answers-only rule the backend applies on
// submit. Null when nothing is rated yet or the scorecard produces no score.
export const liveScore = (
  template: ScorecardFillContext['template'],
  draft: DraftMap,
): number | null => {
  if (template.template_type === 'text') return null;

  const rated = template.questions.filter(
    question =>
      question.question_type === 'rating' &&
      draft[question.id]?.score !== null &&
      draft[question.id]?.score !== undefined,
  );
  if (rated.length === 0) return null;

  const scoreOf = (question: ScorecardQuestion) =>
    draft[question.id]?.score ?? 0;

  if (template.template_type === 'weighted') {
    const weights = rated.reduce(
      (sum, question) => sum + (question.weight ?? 0),
      0,
    );
    if (weights === 0) return null;
    return (
      rated.reduce(
        (sum, question) => sum + scoreOf(question) * (question.weight ?? 0),
        0,
      ) / weights
    );
  }

  return (
    rated.reduce((sum, question) => sum + scoreOf(question), 0) / rated.length
  );
};
